import { GlobalConfigType } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTrialBalance } from 'services'
import { svgLoading } from 'stories/assets'
import { formatDate, thousandSeperator } from 'utils'

import { getAccountTypeLabel, resolveAccountGroup } from '../Accounts/types'

interface ITrialBalanceRow {
  accountNo: number
  accountNumber: string
  accountName: string
  type: string
  section: string
  statement?: string
  totalDebit: number
  totalCredit: number
  netDebit: number
  netCredit: number
  balance: number
}

interface ITrialBalanceTotals {
  netDebit: number
  netCredit: number
  inBalance: boolean
}

const UncategorizedSection = 'Uncategorized'

const formatAmount = (value: number) => {
  if (!value) return '-'
  return `$${thousandSeperator(value)}`
}

const formatBalance = (value: number) => {
  if (!value) return '-'
  if (value < 0) return `($${thousandSeperator(Math.abs(value))})`
  return `$${thousandSeperator(value)}`
}

export const TrialBalance = () => {
  const [isLoading, setLoading] = useState(false)
  const [rows, setRows] = useState<ITrialBalanceRow[]>([])
  const [totals, setTotals] = useState<ITrialBalanceTotals>({ netDebit: 0, netCredit: 0, inBalance: true })
  const [asOfDate, setAsOfDate] = useState('')

  const sectionOptions = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNT_SECTIONS] || [])

  const rowsBySection = useMemo(() => {
    const configuredSections = sectionOptions
      .filter((option: { visible?: boolean; value?: string | null }) => option.visible !== false && option.value)
      .map((option: { value: string }) => option.value)

    const grouped = new Map<string, ITrialBalanceRow[]>()
    const ensureSection = (section: string) => {
      if (!grouped.has(section)) grouped.set(section, [])
    }

    configuredSections.forEach((section: string) => ensureSection(section))
    ensureSection(UncategorizedSection)

    rows.forEach((row) => {
      const section = resolveAccountGroup(row.section, row.type, row.statement)
      ensureSection(section)
      grouped.get(section)!.push(row)
    })

    const orderedSections = [
      ...configuredSections,
      ...Array.from(grouped.keys()).filter(
        (section) => !configuredSections.includes(section) && section !== UncategorizedSection,
      ),
    ]
    if ((grouped.get(UncategorizedSection) || []).length > 0) {
      orderedSections.push(UncategorizedSection)
    }

    return orderedSections
      .filter((section) => (grouped.get(section) || []).length > 0)
      .map((section) => ({
        section,
        rows: grouped.get(section) || [],
      }))
  }, [rows, sectionOptions])

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getTrialBalance()
      setRows(result.results || [])
      setTotals(result.totals || { netDebit: 0, netCredit: 0, inBalance: true })
      setAsOfDate(result.asOfDate || '')
    } catch (error) {
      console.error('Error fetching trial balance:', error)
      setRows([])
      setTotals({ netDebit: 0, netCredit: 0, inBalance: true })
      setAsOfDate('')
    }
    setLoading(false)
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center pb-2">
          Trial Balance
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Every account, balance derived from the General Ledger. Net Debit total must equal Net Credit total.
        {asOfDate && (
          <span className="ml-1 font-medium">As of {formatDate(asOfDate)} (all transactions through today).</span>
        )}
      </p>

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />

        <div className="sm:rounded-lg mb-8 overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-3 py-3">
                  Acct #
                </th>
                <th scope="col" className="px-3 py-3">
                  Account
                </th>
                <th scope="col" className="px-3 py-3">
                  Type
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Total Debit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Total Credit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Net Debit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Net Credit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Balance (Dr-Cr)
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    No accounts found.
                  </td>
                </tr>
              )}
              {rowsBySection.map(({ section, rows: sectionRows }) => (
                <Fragment key={`section-${section}`}>
                  <tr className="bg-blue-100">
                    <td colSpan={8} className="px-3 py-2 font-semibold text-gray-800 uppercase">
                      — {section} —
                    </td>
                  </tr>
                  {sectionRows.map((item) => (
                    <tr
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50"
                      key={item.accountNo}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">{item.accountNumber || item.accountNo}</td>
                      <td className="px-3 py-2">{item.accountName}</td>
                      <td className="px-3 py-2">{getAccountTypeLabel(item.type)}</td>
                      <td className="px-3 py-2 text-right">{formatAmount(item.totalDebit)}</td>
                      <td className="px-3 py-2 text-right">{formatAmount(item.totalCredit)}</td>
                      <td className="px-3 py-2 text-right">{formatAmount(item.netDebit)}</td>
                      <td className="px-3 py-2 text-right">{formatAmount(item.netCredit)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatBalance(item.balance)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                  <td colSpan={5} className="px-3 py-3 text-right uppercase text-xs">
                    Totals
                  </td>
                  <td className="px-3 py-3 text-right">{formatAmount(totals.netDebit)}</td>
                  <td className="px-3 py-3 text-right">{formatAmount(totals.netCredit)}</td>
                  <td className="px-3 py-3 text-right">
                    <span className={totals.inBalance ? 'text-green-700' : 'text-red-600'}>
                      {totals.inBalance ? 'In Balance' : 'Out of Balance'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
